import { HttpErrorResponse } from '@angular/common/http';

const EXPECTED_CLIENT_HTTP_STATUSES = new Set([400, 401, 403, 409]);

export function isHttpErrorResponse(error: unknown): error is HttpErrorResponse {
	return (
		error instanceof HttpErrorResponse ||
		(typeof error === 'object' &&
			error !== null &&
			'status' in error &&
			typeof (error as HttpErrorResponse).status === 'number' &&
			'url' in error)
	);
}

export function httpErrorStatus(error: unknown): number | undefined {
	return isHttpErrorResponse(error) ? error.status : undefined;
}

/** Client-facing HTTP failures that callers handle with toasts or retries. */
export function isExpectedClientHttpError(error: unknown): boolean {
	const status = httpErrorStatus(error);
	return status !== undefined && EXPECTED_CLIENT_HTTP_STATUSES.has(status);
}

/** Google Admin Directory DELETE member when the address is not in the group (idempotent remove). */
export function isGoogleGroupMemberAlreadyRemoved(error: unknown): boolean {
	if (!isHttpErrorResponse(error) || error.status !== 404) {
		return false;
	}

	const body = error.error;
	if (!body || typeof body !== 'object') {
		return false;
	}

	const record = body as Record<string, unknown>;
	if (record['source'] === 'gapi.removeMember') {
		return true;
	}

	const googleError = record['error'];
	if (googleError && typeof googleError === 'object') {
		const code = (googleError as Record<string, unknown>)['code'];
		return code === 404;
	}

	return false;
}

/** User-visible message from an HTTP failure (e.g. Functions 400/500 JSON body). */
export function httpErrorUserMessage(error: unknown, fallback: string): string {
	if (!isHttpErrorResponse(error)) {
		return error instanceof Error && error.message ? error.message : fallback;
	}
	const body = error.error;
	if (typeof body === 'string' && body.trim()) {
		return body;
	}
	if (body && typeof body === 'object') {
		const record = body as Record<string, unknown>;
		if (typeof record['error'] === 'string' && record['error'].trim()) {
			return record['error'];
		}
		if (typeof record['message'] === 'string' && record['message'].trim()) {
			return record['message'];
		}
	}
	return error.message || fallback;
}

export function normalizeErrorForSentry(error: unknown): Error {
	if (error instanceof Error && !isHttpErrorResponse(error)) {
		return error;
	}

	if (isHttpErrorResponse(error)) {
		const bodyMessage =
			typeof error.error === 'string'
				? error.error
				: typeof error.error?.error === 'string'
					? error.error.error
					: typeof error.error?.message === 'string'
						? error.error.message
						: undefined;
		const message = bodyMessage || error.message || error.statusText || `HTTP ${error.status}`;
		const normalized = new Error(`HTTP ${error.status}: ${message}`);
		normalized.name = error.name || 'HttpErrorResponse';
		return normalized;
	}

	if (typeof error === 'string') {
		return new Error(error);
	}

	return new Error(String(error));
}
